import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    BulkActionBar,
    Button,
    Callout,
    DropdownMenu,
    type BulkAction,
    type DropdownMenuItem,
} from '@/components/ui';
import { RoleMapTable } from './RoleMapTable';
import { RoleMapDeleteConfirmModal } from './RoleMapDeleteConfirmModal';
import { RoleMapRepointModal } from './RoleMapRepointModal';
import { RoleMapScopeDiffModal } from './RoleMapScopeDiffModal';
import { RoleMapScopePanel } from './RoleMapScopePanel';
import {
    NoStandardRolesEmptyState,
    RoleMapFilteredEmptyState,
    RoleMapLoadingSkeleton,
    RoleMapNoRowsEmptyState,
} from './EmptyStates';
import type { RoleMapTableVM } from './useRoleMapTable';
import styles from './RoleMapListView.module.css';

/**
 * EAA-1841/EAA-1817: the **Danh sách** view of "Liên kết vai trò" — the flat,
 * sortable, filterable, paginated table with row selection and bulk actions
 * (design frames 56–59, control reference frame 62).
 *
 * EAA-1817 (2026-08-24): the toolbar and pager moved INTO the table, because
 * they are `DataTable`'s own (`showToolbar` / `pagination`) — see
 * `RoleMapTable`'s docstring. The bulk bar and the two banners are the kit's
 * `BulkActionBar` and `Callout` for the same reason: this file used to
 * hand-write all three, and the undo banner in particular shared none of the
 * callout tone palette every other notice on the screen uses.
 *
 * The undo banner lives HERE rather than on the merged page because it reports
 * a write this view made.
 *
 * THE ADD-LINK MODAL MOVED TO THE PAGE (EAA-1817). It used to be mounted here,
 * with the reasoning that `useRoleMapTable`'s queries were disabled while a
 * coverage view was on screen, so a page-level mount would render an empty app
 * list and a permanently disabled submit. Both halves of that stopped being
 * true: the hook is no longer gated (frames 55/55a put "Thêm liên kết" in the
 * title strip of EVERY view), and the button was made visible on every view
 * while its modal stayed here — so on the three coverage views the button did
 * nothing at all. A trigger and the thing it opens have to live at the same
 * level.
 */
export function RoleMapListView({ vm }: { vm: RoleMapTableVM }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [deleteOpen, setDeleteOpen] = useState(false);

    const status = vm.syncStatusQuery.data;
    const scopeLabel =
        vm.scope === 'default'
            ? t('console.roleMap.listView.scopeDefault')
            : (vm.actingOrgName ?? t('console.roleMap.listView.scopeOrgFallback'));

    /** Frame 56's "Áp dụng cho cả 2:" row. Every action acts on the whole
     *  selection, and every one is disabled while a bulk call is in flight. */
    const bulkActions: BulkAction[] = [
        {
            key: 'resync',
            label: t('console.roleMap.bulkBar.resync'),
            onClick: vm.applyBulkResync,
            disabled: vm.bulkBusy,
        },
        {
            key: 'delete',
            label: t('common.delete'),
            tone: 'danger',
            onClick: () => setDeleteOpen(true),
            disabled: vm.bulkBusy,
        },
    ];

    /**
     * EAA-1837: "Đổi phạm vi" is the one bulk action that asks WHICH scope, so
     * it goes in `BulkActionBar`'s exception slot rather than its uniform
     * `actions` list.
     *
     * `RoleMapBulkScopeSerializer` accepts ONLY null (ecosystem default) or the
     * org id already in the URL — a different org's id is refused with
     * VALIDATION_ERROR. So this is a two-option choice by contract, never an
     * org search. The org option is rendered DISABLED rather than actionable:
     * the bar only ever appears while the org slice is shown, so every selected
     * row already carries this org's id and "apply" would be a request that
     * changes nothing yet reports success. Showing it keeps the frame's
     * two-option shape while saying which one is already true.
     */
    const scopeItems: DropdownMenuItem[] = [
        {
            key: 'default',
            label: (
                <span className={styles.scopeOption}>
                    <span>{t('console.roleMap.bulkBar.scopeDefaultLabel')}</span>
                    <small className={styles.scopeOptionHint}>
                        {t('console.roleMap.bulkBar.scopeDefaultHint')}
                    </small>
                </span>
            ),
            onSelect: () => vm.applyBulkScope('default'),
        },
        {
            key: 'org',
            disabled: true,
            label: (
                <span className={styles.scopeOption}>
                    <span>{vm.actingOrgName ?? t('console.roleMap.bulkBar.scopeOrgFallback')}</span>
                    <small className={styles.scopeOptionHint}>
                        {t('console.roleMap.bulkBar.scopeOrgHint')}
                    </small>
                </span>
            ),
        },
    ];

    /* Two counts are deliberately shown, not one. `selectedCount` is the number
       of VISUAL rows ticked; a row can stand for two or more `RoleMap` ids (a
       multi-role mapping), and every action here sends all of them — so when
       the two numbers differ the bar spells out how many links are really about
       to change. Hiding that would let "Xoá 2 dòng" quietly deactivate four. */
    const selectionExpands = vm.selectedIdCount !== vm.selectedCount;

    let body;
    if (vm.loading) {
        body = <RoleMapLoadingSkeleton />;
    } else if (vm.standardRoles.length === 0 && vm.totalLinks === 0) {
        body = <NoStandardRolesEmptyState onCreateLevel={() => navigate('/console/hierarchy')} />;
    } else if (vm.filteredCount === 0 && vm.filtersActive) {
        body = <RoleMapFilteredEmptyState onClear={vm.clearFilters} />;
    } else if (vm.filteredCount === 0) {
        body = (
            <RoleMapNoRowsEmptyState
                scopeLabel={scopeLabel}
                canAdd={vm.canManageCurrentScope}
                onAdd={vm.openAdd}
            />
        );
    } else {
        body = <RoleMapTable vm={vm} />;
    }

    return (
        // EAA-1817: was a bare fragment directly inside `.role-map-card`, which gave
        // toolbar/bulk-bar/banner/table their `gap` rhythm via ITS OWN flex
        // column. `.role-map-card` is gone (page chrome moved onto
        // `PageHeader`/`SectionCard`) — this wrapper owns that same rhythm now,
        // self-contained regardless of what ends up wrapping this component.
        <div className={styles.listBody}>
            <RoleMapScopePanel vm={vm} />

            {/* EAA-1817: `vm.bulkDisabledReason` used to be its own box here. It
                moved INTO `RoleMapScopePanel` — it is a fact about the selected
                scope, and three stacked notices above the table was most of what
                the reader saw before the first row. Nothing was dropped; see that
                component's docstring. */}

            <BulkActionBar
                count={vm.selectedCount}
                countLabel={t('console.roleMap.bulkBar.selectedCount', {
                    count: vm.selectedCount,
                })}
                applyLabel={t('console.roleMap.bulkBar.applyToAll', { count: vm.selectedCount })}
                actions={bulkActions}
                onClear={vm.clearSelection}
                clearLabel={t('console.roleMap.bulkBar.clearSelection')}
                label={t('console.roleMap.bulkBar.ariaLabel')}
            >
                <DropdownMenu items={scopeItems} placement="bottomLeft">
                    <Button size="small" disabled={vm.bulkBusy}>
                        {t('console.roleMap.bulkBar.changeScope')}
                    </Button>
                </DropdownMenu>
                {selectionExpands && (
                    <span className={styles.bulkBarExpands}>
                        {t('console.roleMap.bulkBar.expandsText', {
                            count: vm.selectedIdCount,
                        })}
                    </span>
                )}
            </BulkActionBar>

            {status?.status === 'degraded' && (
                <Callout
                    tone="danger"
                    label={t('console.roleMap.listView.degradedBannerLabel')}
                    longLabel
                >
                    {t('console.roleMap.listView.degradedBannerText', {
                        count: status.paused_count,
                    })}
                </Callout>
            )}

            {/* EAA-1817: the one region that grows/scrolls inside `.role-map-list-body`
                — the scope panel, bulk bar and banners above stay their natural
                height (default flex-grow:0), this absorbs the rest per the
                fit-to-shell rule. */}
            <div className={styles.listScroll}>{body}</div>

            <RoleMapDeleteConfirmModal
                open={deleteOpen}
                groups={vm.selectedGroups}
                linkCount={vm.selectedIdCount}
                pending={vm.bulkBusy}
                onCancel={() => setDeleteOpen(false)}
                onConfirm={() => {
                    setDeleteOpen(false);
                    vm.applyBulkDelete();
                }}
            />

            <RoleMapRepointModal vm={vm} />
            <RoleMapScopeDiffModal vm={vm} />

            {/* EAA-1817: "Hoàn tác" after a delete — see `useRoleMapWrites.ts`'s
                docstring on `undoPending` for why this is a separate element
                rather than an action button riding inside `useToast()`'s
                notification (the shared `ToastApi` has no slot for one). No
                server-side expiry backs this window; it is a purely FRONTEND
                courtesy that stops offering the button after ~10s, not a claim
                that restoring becomes impossible after that.

                It is a kit `Callout` with its new `actions` slot rather than the
                hand-built `.role-map-undo-banner` it was: that banner drew its
                own fill and border, so it was the one notice on this screen that
                did not follow the tone palette. */}
            {vm.undo.pending && (
                <Callout
                    tone="info"
                    className={styles.undoBanner}
                    actions={
                        <>
                            <Button onClick={vm.undo.restore} disabled={vm.undo.busy}>
                                {vm.undo.busy
                                    ? t('console.roleMap.listView.undoRestoring')
                                    : t('console.roleMap.listView.undoRestore')}
                            </Button>
                            <Button
                                onClick={vm.undo.dismiss}
                                aria-label={t('console.roleMap.listView.undoDismissAria')}
                            >
                                ×
                            </Button>
                        </>
                    }
                >
                    {t('console.roleMap.listView.undoBannerText', {
                        label: vm.undo.pending.label,
                    })}
                </Callout>
            )}
        </div>
    );
}

export default RoleMapListView;
